/**
 * BFF Apps navigation.
 *
 * Flat entries grouped by fixed section headings, matching the redesign. Every row carries an
 * `isToAppear` fed by {@link usePrivileges}, which reads the Flagsmith flags — nothing appears
 * by default, and a disabled row is removed rather than greyed out.
 *
 * Section headings survive only while at least one item under them does; see
 * {@link filterNavEntries}.
 *
 * @module config
 */

import { env } from '@/config/env';
import { usePrivileges } from '@/Hooks/usePrivileges/usePrivilleges';
import type { NavEntry, NavItem } from '@/Shared/components/SidebarNav/SidebarNav';
import { filterNavEntries } from '@/Shared/components/SidebarNav/SidebarNav';

/** Logo shown at the top of the sidebar. */
export interface SidebarLogo {
  light: string;
  dark: string;
}

interface DashboardSidebarConfig {
  entries: NavEntry[];
  /** `id` of the entry matching the current route, if any. */
  activeId?: string;
  logo: SidebarLogo;
}

/**
 * Builds the sidebar entries for the current user.
 *
 * @param currentPath - Router pathname, used to mark the active row.
 * @param pendingWhatsNewCount - Announcements awaiting approval, shown as the What's New badge.
 */
export const useDashboardSidebarConfig = (
  currentPath: string,
  pendingWhatsNewCount = 0,
): DashboardSidebarConfig => {
  const { pages } = usePrivileges();

  const rawEntries: NavEntry[] = [
    {
      kind: 'item',
      id: 'overview',
      label: 'Visão geral',
      icon: 'Misc.House',
      path: '/dashboard',
      isToAppear: pages.DASHBOARD.PAGE,
    },
    { kind: 'section', label: 'Business Units' },
    {
      kind: 'item',
      id: 'bus',
      label: 'Gerenciar BUs',
      icon: 'Misc.Buildings',
      path: '/business-units',
      isToAppear: pages.BUSINESS_UNITS.PAGE,
    },
    {
      kind: 'item',
      id: 'whats-new',
      label: "What's New",
      icon: 'Communication.Newspaper',
      path: '/business-units/whats-new',
      badge: pendingWhatsNewCount,
      isToAppear: pages.BUSINESS_UNITS.WHATS_NEW.PAGE,
    },
    {
      kind: 'item',
      id: 'pages',
      label: 'Páginas',
      icon: 'Menu.SquaresFour',
      path: '/business-units/pages',
      isToAppear: pages.BUSINESS_UNITS.PAGES.PAGE,
    },
    {
      kind: 'item',
      id: 'tours',
      label: 'Tours',
      icon: 'Misc.Broadcast',
      path: '/business-units/tours',
      isToAppear: pages.BUSINESS_UNITS.TOURS.PAGE,
    },
    {
      kind: 'item',
      id: 'opt-in-features',
      label: 'Funcionalidades opt-in',
      icon: 'Misc.Lightbulb',
      path: '/opt-in-features',
      isToAppear: pages.BUSINESS_UNITS.OPT_IN_FEATURES.PAGE,
    },
    {
      kind: 'item',
      id: 'surveys',
      label: 'Pesquisas',
      icon: 'Communication.ChatCircleText',
      path: '/surveys',
      isToAppear: pages.BUSINESS_UNITS.SURVEYS.PAGE,
    },
    { kind: 'section', label: 'Operação' },
    {
      kind: 'item',
      id: 'signatures',
      label: 'Assinaturas',
      icon: 'Misc.Key',
      path: '/signatures',
      isToAppear: pages.SIGNATURES.PAGE,
    },
    { kind: 'section', label: 'Administração' },
    {
      kind: 'item',
      id: 'users',
      label: 'Usuários',
      icon: 'Users.Users',
      path: '/settings/users',
      isToAppear: pages.SETTINGS.USERS.PAGE,
    },
  ];

  const entries = filterNavEntries(rawEntries);

  /**
   * Longest matching path wins, so `/business-units/pages` marks Páginas rather than the
   * Gerenciar BUs row it is nested under.
   */
  const activeId = entries
    .filter((entry): entry is NavItem => entry.kind === 'item')
    .filter(item => currentPath === item.path || currentPath.startsWith(`${item.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0]?.id;

  const logo: SidebarLogo = {
    light: `${env.cdnURL}/Utilities/Water/logo-utilities.webp`,
    dark: `${env.cdnURL}/Utilities/Water/logo-utilities.webp`,
  };

  return { entries, activeId, logo };
};
